import ManageSearchOutlined from '@mui/icons-material/ManageSearchOutlined'
import MoreIcon from '@mui/icons-material/MoreHoriz'
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab'
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import Collapse from '@mui/material/Collapse'
import {
  ROLES_ARVAL,
  ROLE_ADMIN,
  ROLE_B2C,
  ROLE_LOGISTICS,
  ROLE_PARTNER_ADMIN,
  ROLE_POST_SALES,
  ROLE_SUPER_ADMIN,
  useAuth,
} from 'auth'
import { addYears, differenceInDays, format } from 'date-fns'
import { Form, Formik } from 'formik'
import getOption, { OPTION_ALL_VALUE } from 'lib/getOption'
import getYearMonthDayDate from 'lib/getYearMonthDayDate'
import {
  // INVENTORY_STATE_ACQUIRED_B2B,
  INVENTORY_STATE_ASSIGNED,
  INVENTORY_STATE_EXPIRED,
  INVENTORY_STATE_IN_TRATTATIVA_RELEASE,
  INVENTORY_STATE_PARKED,
  INVENTORY_STATE_PREASSIGNED,
  INVENTORY_STATE_PRESOLD,
  INVENTORY_STATE_PROLONGED,
  INVENTORY_STATE_RELEASE,
  // INVENTORY_STATE_RESOLD_B2B,
  INVENTORY_STATE_RETURNED,
  INVENTORY_STATE_RETURNED_BY_ARVAL,
  INVENTORY_STATE_RETURNED_BY_PARTNER,
  INVENTORY_STATE_SOLD,
  INVENTORY_STATE_VALIDATO_RISCHIO,
  INVENTORY_STATE_VALUTAZIONE_RELEASE,
} from 'lib/inventoryStates'
import {
  TABLE_ROWS_PER_PAGE_INVENTORY,
  TABLE_ROWS_PER_PAGE_OPTIONS,
} from 'lib/tableLib'
import T from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'
import * as Yup from 'yup'

import ClipboardWithTrendUp from 'components/atoms/ClipboardWithTrendUp'
import ConfirmationDialog from 'components/atoms/ConfirmationDialog'
import ConfirmBar from 'components/atoms/ConfirmBar'
import DateCell from 'components/atoms/DateCell'
import DotCell from 'components/atoms/DotCell'
import DownloadBar from 'components/atoms/DownloadBar'
import FilterDateRange from 'components/atoms/FilterDateRange'
import FilterRange from 'components/atoms/FilterRange'
import FilterSelect from 'components/atoms/FilterSelect'
import FormDatePicker from 'components/atoms/FormDatePicker'
import FormTextField from 'components/atoms/FormTextField'
import LabelledSwitch from 'components/atoms/LabelledSwitch'
import { LoadingBackdrop } from 'components/atoms/LoadingBackdrop'
import LogIcon from 'components/atoms/LogIcon'
import ReleaseReportBar from 'components/atoms/ReleaseReportBar'
import SearchField from 'components/atoms/SearchField'
import StaticDatePicker from 'components/atoms/StaticDatePicker'
import Table from 'components/atoms/Table'
import TableHeaderCell from 'components/atoms/TableHeaderCell'
import { BLIND_ORIGIN_NO_BLIND } from 'components/organisms/Assignment/assignmentLib'

import useCheckboxSelection from 'hooks/useCheckboxSelection'
import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useEntityData from 'hooks/useEntityData'
import useExport from 'hooks/useExport'
import useLogs from 'hooks/useLogs'
import useReleaseReport from 'hooks/useReleaseReport'
import useReport from 'hooks/useReport'
import useTablePagination from 'hooks/useTablePagination'

import InventoryExpiresAtCell from './InventoryExpiresAtCell'
import useInventory from './useInventory'
import usePartnerInventoryFilters from './usePartnerInventoryFilters'
const ACTION_RETURN_FROM_PARTNER = 'return-from-partner'
const ACTION_RETURN_TO_PARTNER = 'return-to-partner'
const ACTION_UPDATE_EXPIRES = 'update-expires'
const ACTION_RETURN_FROM_ARVAL = 'return-from-arval'
const ACTION_RETURN = 'return'
const ACTION_EDIT_DATES = 'edit-dates'
const ACTION_RESTORATION_REVERSE = 'restoration-reverse'
const ACTION_PARKED = 'parked'
const ACTION_PARKED_REVERSE = 'parked-reverse'
const ACTION_ADD_COMMENT = 'add-comment'
// REL-F03: standalone "commenti Release" write action. Il valore stringa ==
// suffisso URL della PATCH (/inventory/add-release-comment).
const ACTION_ADD_RELEASE_COMMENT = 'add-release-comment'
const ACTION_RESO_TECNICO = 'reso-tecnico'
const ACTION_DOWNLOAD_REPORT = 'download-report'
// Lifecycle Release (REL-F02): il valore stringa == suffisso URL della PATCH,
// così updateInventory(action, ...) colpisce l'endpoint giusto senza mappe.
// Forward (#1/#3/#5/#7) = commento obbligatorio; #5/#7 anche outcome.
// Reverse (#2/#4/#6/#8) = nessun form. Gating in actionItemsEnabled.
const ACTION_IN_TRATTATIVA_RELEASE = 'in-trattativa-release'
const ACTION_IN_TRATTATIVA_RELEASE_REVERSE = 'in-trattativa-release-reverse'
const ACTION_VALUTAZIONE_RELEASE = 'valutazione-release'
const ACTION_VALUTAZIONE_RELEASE_REVERSE = 'valutazione-release-reverse'
const ACTION_VALIDATO_RISCHIO = 'validato-rischio'
const ACTION_VALIDATO_RISCHIO_REVERSE = 'validato-rischio-reverse'
const ACTION_RELEASE = 'release'
const ACTION_RELEASE_REVERSE = 'release-reverse'

// Finestre temporali (giorni dalla data di disponibilità). Il BE resta l'autorità:
// qui gatiamo solo la visibilità del bottone per il profilo Partner.
// Parcheggiata (spec v0.2): dal giorno 41 al giorno 90. Arval: nessun limite.
const PARKED_DAY_MIN = 41
const PARKED_DAY_MAX = 90
// Reso tecnico (spec v0.2): dal giorno 1 al limite per-partner. Arval: nessun limite.
const RESO_TECNICO_DAY_MIN = 1

const stateCell = ({ value, row }, t) => {
  return (
    <DotCell
      label={t(`partnerInventory.status.${value}`)}
      color={
        [
          INVENTORY_STATE_ASSIGNED,
          INVENTORY_STATE_PREASSIGNED,
          INVENTORY_STATE_PROLONGED,
        ].includes(value)
          ? row.original.daysToExpire > 7
            ? 'textPrimary'
            : 'warning'
          : [INVENTORY_STATE_SOLD, INVENTORY_STATE_PRESOLD].includes(value)
          ? 'primary'
          : [INVENTORY_STATE_EXPIRED].includes(value)
          ? 'error'
          : 'textSecondary'
      }
    />
  )
}

const overviewAuditCell = (data, t) => {
  const { value, row } = data
  return (
    <DotCell
      label={t(`partnerInventory.overviewAudit.${value}`)}
      color={
        [
          INVENTORY_STATE_ASSIGNED,
          INVENTORY_STATE_PREASSIGNED,
          INVENTORY_STATE_PROLONGED,
        ].includes(row.original.state)
          ? row.original.daysToExpire > 7
            ? 'textPrimary'
            : 'warning'
          : [INVENTORY_STATE_SOLD, INVENTORY_STATE_PRESOLD].includes(
              row.original.state
            )
          ? 'primary'
          : [INVENTORY_STATE_EXPIRED].includes(row.original.state)
          ? 'error'
          : 'textSecondary'
      }
    />
  )
}

const CommentCell = ({ data, testId = 'show-comments', ...restProp }) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState(null)

  const handlePopoverOpen = (e) => setAnchorEl(e.currentTarget)
  const handlePopoverClose = () => setAnchorEl(null)

  const open = Boolean(anchorEl)

  const actionColors = {
    addComment: '#FFC75F',
    editDates: '#FF6F91',
    returnCar: '#845EC2',
    // REL-F03: distinct dot for the segregated "commenti Release" stream.
    addCommentiRelease: '#00C9A7',
  }

  return data.length > 0 ? (
    <Grid sx={{ overflow: 'auto' }}>
      <ManageSearchOutlined
        sx={{ color: 'gray' }}
        data-testid={testId}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        disabled={data.length === 0}
      />

      <Popover
        id={`popover-${data.inventoryId}`}
        style={{ zIndex: 9999, pointerEvents: 'none' }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Timeline position="right">
          {data.map((comment) => {
            const date = format(new Date(comment.createdAt), 'dd/MM/yyyy HH:mm')
            const user = `${comment.firstName} ${comment.lastName}`

            return (
              <TimelineItem key={`timeline-${comment.commentId}`}>
                <TimelineOppositeContent
                  sx={{ m: 'auto 0', minWidth: 140 }}
                  align="right"
                  variant="body2"
                  color="text.secondary"
                >
                  <Typography variant="body2">{user}</Typography>
                  <Typography variant="body2">{date}</Typography>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineConnector />
                  <TimelineDot
                    sx={{ backgroundColor: actionColors[comment.action] }}
                  />
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    px: 2,
                    minWidth: 140,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="span"
                  >
                    {t(`common.logs.action.${comment.action}`)}
                  </Typography>
                  <Typography variant="body1" sx={{ pt: 0.5 }}>
                    {comment.comment}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            )
          })}
        </Timeline>
      </Popover>
    </Grid>
  ) : (
    <></>
  )
}

const createColumns = (
  items,
  arvalUser,
  countExpired,
  // noActionStates,
  isSelected,
  handleSelectAllClick,
  selected,
  readOnly,
  contentIsReadOnly,
  partnerInventoryFilters,
  t
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const {
    filters,
    filtersData,
    setPlateQuery,
    setCommentsQuery,
    setReleaseCommentsQuery,
    // setSessionIdQuery,
    setSessionIdRange,
    // setStoragePlaceQuery,
    setDaysRange,
    setPreviewDaysRange,
    setPriceRange,
    setWithdrawalPenaltyRange,
    setFeePreviewRange,
    setRestitutionPenaltyRange,
    setAssignedAtRange,
    setStateChangedAtRange,
    setExpiresAtRange,
    setAvailabilityAtRange,
    setRestored,
    setReleaseFlag,
    setStoragePlaces,
    setPartners,
    setBrands,
    setModels,
    setOverviewAudit,
    setStates,
    setMandatoryToBuy,
  } = partnerInventoryFilters

  const numberFormatter = new Intl.NumberFormat('it-IT')

  const actionItems = items
  //   .filter(
  //   (item) => !noActionStates.includes(item.state)
  // )

  const allOption = getOption(t('common.form.all'), OPTION_ALL_VALUE)
  const result = [
    ...(!contentIsReadOnly
      ? [
          {
            accessor: 'checkbox',
            Header: () => {
              if (!!contentIsReadOnly) {
                return <></>
              } else {
                return (
                  <Stack direction="column" height={1} justifyContent="center">
                    <Checkbox
                      data-testid="checkbox-select-all"
                      indeterminate={
                        selected.length > 0 &&
                        selected.length < actionItems.length
                      }
                      checked={
                        !readOnly &&
                        actionItems.every((item) =>
                          isSelected(item, 'inventoryId')
                        )
                      }
                      onChange={(e) => handleSelectAllClick(e)}
                      disabled={readOnly}
                    />
                  </Stack>
                )
              }
            },
            Cell: ({ value, row }) => {
              if (!!contentIsReadOnly) {
                return <></>
              } else {
                return (
                  <Stack direction="column" height={1} justifyContent="center">
                    <Checkbox
                      color="primary"
                      checked={isSelected(row.original, 'inventoryId')}
                      inputProps={{
                        'aria-labelledby': row.original.plate,
                      }}
                      // disabled={
                      //   noActionStates.includes(row.original.state) || readOnly
                      // }
                    />
                  </Stack>
                )
              }
            },
            headerProps: {
              padding: 'checkbox',
              component: 'th',
            },
            cellProps: {
              padding: 'checkbox',
              component: 'th',
            },
            sticky: 'left',
            width: 52,
          },
        ]
      : []),
    {
      accessor: 'plate',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.plate')}
          filter={
            <SearchField
              data-testid="text-search-plate"
              placeholder={t(COMMON_FORM_SEARCH)}
              value={filters?.plateQuery}
              onChange={(event) => setPlateQuery(event.target.value)}
              fullWidth={false}
              sx={{ width: (theme) => theme.spacing(15) }}
            />
          }
        />
      ),
      ...(!arvalUser ? { sticky: 'left' } : {}),
    },
    {
      accessor: 'brand',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.brand')}
          filter={
            <FilterSelect
              data-testid="filter-brands"
              allOption={allOption}
              options={filtersData.brands}
              value={filters.brands}
              handleChange={setBrands}
              multiple
            />
          }
        />
      ),
    },
    {
      accessor: 'model',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.model')}
          filter={
            <FilterSelect
              allOption={allOption}
              options={filtersData.models}
              value={filters.models}
              handleChange={setModels}
              multiple
            />
          }
        />
      ),
    },
    {
      accessor: 'sessionId',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.sessionId')}
          filter={
            // <SearchField
            //   data-testid="text-search-session-id"
            //   placeholder={t('common.form.searchSessionId')}
            //   value={filters.sessionIdQuery}
            //   onChange={(event) => setSessionIdQuery(event.target.value)}
            //   fullWidth={false}
            //   sx={{ width: (theme) => theme.spacing(15) }}
            // />
            <FilterRange
              data-testid="range-sessionId"
              minBound={filtersData.sessionIdMin}
              maxBound={filtersData.sessionIdMax}
              min={filters.sessionIdMin}
              max={filters.sessionIdMax}
              handleRangeChange={setSessionIdRange}
              usePadding={true}
            />
          }
        />
      ),
    },
    {
      accessor: 'assignedAt',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.assignedAt')}
          filter={
            <FilterDateRange
              value={[filters.assignedAtFrom, filters.assignedAtTo]}
              handleChange={setAssignedAtRange}
            />
          }
        />
      ),
      Cell: DateCell,
    },
    {
      accessor: 'days',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.days')}
          filter={
            <FilterRange
              minBound={filtersData.daysMin}
              maxBound={filtersData.daysMax}
              min={filters.daysMin}
              max={filters.daysMax}
              handleRangeChange={setDaysRange}
            />
          }
        />
      ),
    },
    {
      accessor: 'previewDays',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.previewDays')}
          filter={
            <FilterRange
              minBound={filtersData.previewDaysMin}
              maxBound={filtersData.previewDaysMax}
              min={filters.previewDaysMin}
              max={filters.previewDaysMax}
              handleRangeChange={setPreviewDaysRange}
            />
          }
        />
      ),
      Cell: ({ value }) =>
        value && value >= 0 ? numberFormatter.format(value) : '-',
    },
    {
      accessor: 'state',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.status')}
          filter={
            <FilterSelect
              allOption={allOption}
              options={filtersData.states}
              value={filters.states}
              handleChange={setStates}
              multiple
            />
          }
        />
      ),
      Cell: (data) => stateCell(data, t),
    },
    {
      accessor: 'releaseFlag',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.releaseFlag')}
          filter={
            <FilterSelect
              data-testid="filter-release-flag"
              allOption={allOption}
              options={filtersData.releaseFlag}
              value={filters.releaseFlag}
              handleChange={setReleaseFlag}
            />
          }
        />
      ),
      Cell: ({ value }) =>
        typeof value === 'boolean' ? t(`common.table.boolean.${value}`) : '-',
    },
    {
      accessor: 'opzioneReleaseAt',
      Header: (
        <TableHeaderCell label={t('partnerInventory.table.opzioneReleaseAt')} />
      ),
      Cell: DateCell,
    },
    {
      accessor: 'releaseAt',
      Header: <TableHeaderCell label={t('partnerInventory.table.releaseAt')} />,
      Cell: DateCell,
    },
    {
      accessor: 'mandatoryToBuy',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.mandatoryToBuy')}
          filter={
            <FilterSelect
              allOption={allOption}
              options={filtersData.mandatoryToBuy}
              value={filters.mandatoryToBuy}
              handleChange={setMandatoryToBuy}
            />
          }
        />
      ),
      Cell: ({ value }) => t(`common.table.boolean.${!!value}`),
    },
    {
      accessor: 'stateChangedAt',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.stateChangedAt')}
          filter={
            <FilterDateRange
              value={[filters.stateChangedAtFrom, filters.stateChangedAtTo]}
              handleChange={setStateChangedAtRange}
            />
          }
        />
      ),
      Cell: DateCell,
    },
    {
      accessor: 'expiresAt',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.expiresAt')}
          errorsNumber={countExpired}
          filter={
            <FilterDateRange
              value={[filters.expiresAtFrom, filters.expiresAtTo]}
              handleChange={setExpiresAtRange}
            />
          }
        />
      ),
      Cell: InventoryExpiresAtCell,
    },
    {
      accessor: 'storagePlace',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.storagePlace')}
          filter={
            <FilterSelect
              data-testid="text-search-storage-place"
              allOption={allOption}
              options={filtersData.storagePlaces}
              value={filters.storagePlaces}
              handleChange={setStoragePlaces}
              multiple
            />
          }
        />
      ),
      Cell: ({ value }) =>
        value !== null && value !== undefined ? value : '-',
    },
    // {
    //   accessor: 'storagePlace',
    //   Header: (
    //     <TableHeaderCell
    //       label={t('partnerInventory.table.storagePlace')}
    //       filter={
    //         <SearchField
    //           data-testid="text-search-storage-place"
    //           placeholder={t('common.form.searchSessionId')}
    //           value={filters.storagePlaceQuery}
    //           onChange={(event) => setStoragePlaceQuery(event.target.value)}
    //           fullWidth={false}
    //           sx={{ width: (theme) => theme.spacing(15) }}
    //         />
    //       }
    //     />
    //   ),
    // },
    {
      accessor: 'price',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.price')}
          filter={
            <FilterRange
              minBound={filtersData.priceMin}
              maxBound={filtersData.priceMax}
              min={filters.priceMin}
              max={filters.priceMax}
              handleRangeChange={setPriceRange}
            />
          }
        />
      ),
      Cell: ({ value }) => (value && numberFormatter.format(value)) || null,
    },
    {
      accessor: 'withdrawalPenalty',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.withdrawalPenalty')}
          filter={
            <FilterRange
              minBound={filtersData.withdrawalPenaltyMin}
              maxBound={filtersData.withdrawalPenaltyMax}
              min={filters.withdrawalPenaltyMin}
              max={filters.withdrawalPenaltyMax}
              handleRangeChange={setWithdrawalPenaltyRange}
            />
          }
        />
      ),
      Cell: ({ value }) =>
        value !== null && value !== undefined ? value : '-',
    },
    {
      accessor: 'feePreview',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.feePreview')}
          filter={
            <FilterRange
              minBound={filtersData.feePreviewMin}
              maxBound={filtersData.feePreviewMax}
              min={filters.feePreviewMin}
              max={filters.feePreviewMax}
              handleRangeChange={setFeePreviewRange}
            />
          }
        />
      ),
      Cell: ({ value }) =>
        value !== null && value !== undefined ? value : '-',
    },
    {
      accessor: 'restitutionPenalty',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.restitutionPenalty')}
          filter={
            <FilterRange
              minBound={filtersData.restitutionPenaltyMin}
              maxBound={filtersData.restitutionPenaltyMax}
              min={filters.restitutionPenaltyMin}
              max={filters.restitutionPenaltyMax}
              handleRangeChange={setRestitutionPenaltyRange}
            />
          }
        />
      ),
      Cell: ({ value }) =>
        value !== null && value !== undefined ? value : '-',
    },
    ...(arvalUser || (items.length && items[0].partnerRestoration)
      ? [
          {
            accessor: 'restored',
            Header: (
              <TableHeaderCell
                label={t('partnerInventory.table.restored')}
                filter={
                  <FilterSelect
                    allOption={allOption}
                    options={filtersData.restored}
                    value={filters.restored}
                    handleChange={setRestored}
                  />
                }
              />
            ),
            Cell: ({ value }) =>
              typeof value === 'boolean'
                ? t(`common.table.boolean.${value}`)
                : '-',
          },
        ]
      : []),
    {
      accessor: 'availabilityAt',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.availabilityAt')}
          filter={
            <FilterDateRange
              value={[filters.availabilityAtFrom, filters.availabilityAtTo]}
              handleChange={setAvailabilityAtRange}
            />
          }
        />
      ),
      Cell: DateCell,
    },
    {
      accessor: 'overviewAudit',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.overviewAudit')}
          filter={
            <FilterSelect
              allOption={allOption}
              options={filtersData.overviewAudit}
              value={filters.overviewAudit}
              handleChange={setOverviewAudit}
              multiple
            />
          }
        />
      ),
      Cell: (data) => overviewAuditCell(data, t),
    },
    {
      accessor: 'comments',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.comments')}
          filter={
            <SearchField
              data-testid="text-search-comments"
              placeholder={t(COMMON_FORM_SEARCH)}
              value={filters?.commentsQuery}
              onChange={(event) => setCommentsQuery(event.target.value)}
              fullWidth={false}
              sx={{ width: (theme) => theme.spacing(15) }}
            />
          }
        />
      ),
      Cell: ({ value }) => <CommentCell data={value} />,
    },
    {
      // REL-F03: timeline "commenti Release", speculare alla colonna NOTE ma
      // sullo stream segregato (others.releaseComments). Ricerca dedicata
      // action-scoped lato BE (releaseCommentsQuery).
      accessor: 'releaseComments',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.releaseComments')}
          filter={
            <SearchField
              data-testid="text-search-release-comments"
              placeholder={t(COMMON_FORM_SEARCH)}
              value={filters?.releaseCommentsQuery}
              onChange={(event) => setReleaseCommentsQuery(event.target.value)}
              fullWidth={false}
              sx={{ width: (theme) => theme.spacing(15) }}
            />
          }
        />
      ),
      Cell: ({ value }) => (
        <CommentCell data={value} testId="show-release-comments" />
      ),
    },
    {
      accessor: 'resoTecnico',
      Header: (
        <TableHeaderCell label={t('partnerInventory.table.resoTecnico')} />
      ),
      Cell: ({ value }) => (value ? 'SI' : '—'),
    },
  ]

  if (arvalUser) {
    result.splice(1, 0, {
      accessor: 'partner',
      Header: (
        <TableHeaderCell
          label={t('partnerInventory.table.partner')}
          filter={
            <FilterSelect
              allOption={allOption}
              options={filtersData.partners}
              value={filters.partners}
              handleChange={setPartners}
              multiple
            />
          }
        />
      ),
    })
  }

  return result
}

const COMMON_REQUIRED_FIELD = 'common.requiredField'
const COMMON_FORM_SEARCH = 'common.form.search'

// eslint-disable-next-line sonarjs/cognitive-complexity
const PartnerInventoryTable = ({ contentIsReadOnly = false }) => {
  const { t } = useTranslation()

  const { saveLogs, isFetching: isFetchingLogs } = useLogs()

  const { saveReport, isFetching: isFetchingReport } = useReport()

  const { saveExport, isFetching: isFetchingExport } = useExport()

  const { saveReleaseReport, isFetching: isFetchingReleaseReport } =
    useReleaseReport()

  const { hasOneOfRoles } = useAuth()

  const partnerInventoryFilters = usePartnerInventoryFilters()

  const { filters, setSearchQuery, setSort, initFilters } =
    partnerInventoryFilters

  const [action, setAction] = useState()
  const [dateValue, setDateValue] = useState(null)
  const [startDateValue, setStartDateValue] = useState()
  const [restored, setRestored] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [initialDateValues, setInitialDateValues] = useState({
    returnedAt: null,
    exitedAt: null,
    comment: '',
  })
  const [initialDownloadReportValues, setInitialDownloadReportValues] =
    useState({
      startDate: null,
      endDate: null,
    })

  const DatesSchema = useMemo(() => {
    const date = Yup.date().typeError(t('common.date.error.invalidFormat'))
    return Yup.object().shape({
      ...(action === ACTION_RETURN
        ? {
            returnedAt: date.required(t(COMMON_REQUIRED_FIELD)),
            exitedAt: date.nullable().default(() => null),
          }
        : {
            ...(initialDateValues.exitedAt
              ? {
                  exitedAt: date.required(t(COMMON_REQUIRED_FIELD)),
                }
              : {}),
            ...(initialDateValues.returnedAt
              ? {
                  returnedAt: date.required(t(COMMON_REQUIRED_FIELD)),
                }
              : {}),
          }),
      comment: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
    })
  }, [t, action, initialDateValues])

  const DownloadReportFormSchema = useMemo(() => {
    const date = Yup.date().typeError(t('common.date.error.invalidFormat'))
    return Yup.object().shape({
      startDate: date.required(t(COMMON_REQUIRED_FIELD)),
      endDate: date.required(t(COMMON_REQUIRED_FIELD)),
    })
  }, [t])

  const ResoTecnicoSchema = useMemo(
    () =>
      Yup.object().shape({
        mode: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
        cluster: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
        detail: Yup.string().when('cluster', {
          is: 'meccanica',
          then: (schema) => schema.required(t(COMMON_REQUIRED_FIELD)),
          otherwise: (schema) => schema.nullable(),
        }),
        comment: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
      }),
    [t]
  )

  // REL-F03: commento obbligatorio per lo stream "commenti Release" standalone
  // (BE addReleaseCommentSchema: minLength 1). Nessun outcome (a differenza dei
  // forward della lifecycle Release).
  const AddReleaseCommentSchema = useMemo(
    () =>
      Yup.object().shape({
        comment: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
      }),
    [t]
  )

  // Lifecycle Release (REL-F02): commento obbligatorio su tutti i forward;
  // l'outcome è richiesto solo su Validato Rischio (ok/ko) e Release
  // (confirm/rinuncia). Il BE resta l'autorità (minLength 1 + enum server-side).
  const ReleaseTransitionSchema = useMemo(
    () =>
      Yup.object().shape({
        comment: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
        ...(action === ACTION_VALIDATO_RISCHIO || action === ACTION_RELEASE
          ? { outcome: Yup.string().required(t(COMMON_REQUIRED_FIELD)) }
          : {}),
      }),
    [t, action]
  )

  const {
    page: newPage,
    rowsPerPage: newRowsPerPage,
    handleChangePage: handleChangeTablePage,
    handleChangeRowsPerPage: handleChangeTableRowsPerPage,
  } = useTablePagination({ rowsPerPage: TABLE_ROWS_PER_PAGE_INVENTORY })

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const prepareFilters = useCallback((filters, filtersData) => {
    const daysNotFiltering =
      filters?.daysMin === filtersData?.daysMin &&
      filters?.daysMax === filtersData?.daysMax
    const priceNotFiltering =
      filters?.priceMin === filtersData?.priceMin &&
      filters?.priceMax === filtersData?.priceMax
    const withdrawalPenaltyNotFiltering =
      filters?.withdrawalPenaltyMin === filtersData?.withdrawalPenaltyMin &&
      filters?.withdrawalPenaltyMax === filtersData?.withdrawalPenaltyMax
    const feePreviewNotFiltering =
      filters?.feePreviewMin === filtersData?.feePreviewMin &&
      filters?.feePreviewMax === filtersData?.feePreviewMax
    const restitutionPenaltyNotFiltering =
      filters?.restitutionPenaltyMin === filtersData?.restitutionPenaltyMin &&
      filters?.restitutionPenaltyMax === filtersData?.restitutionPenaltyMax
    return {
      ...filters,
      brands: filters?.brands?.map((item) => {
        return item === 'all' ? 'all' : filtersData?.brands?.[item + 1].label
      }),
      models: filters?.models?.map((item) => {
        return item === 'all' ? 'all' : filtersData?.models?.[item + 1].label
      }),
      daysMin: daysNotFiltering ? 0 : filters?.daysMin,
      daysMax: daysNotFiltering ? 0 : filters?.daysMax,
      priceMin: priceNotFiltering ? 0 : filters?.priceMin,
      priceMax: priceNotFiltering ? 0 : filters?.priceMax,
      withdrawalPenaltyMin: withdrawalPenaltyNotFiltering
        ? 0
        : filters?.withdrawalPenaltyMin,
      withdrawalPenaltyMax: withdrawalPenaltyNotFiltering
        ? 0
        : filters?.withdrawalPenaltyMax,
      feePreviewMin: feePreviewNotFiltering ? 0 : filters?.feePreviewMin,
      feePreviewMax: feePreviewNotFiltering ? 0 : filters?.feePreviewMax,
      restitutionPenaltyMin: restitutionPenaltyNotFiltering
        ? 0
        : filters?.restitutionPenaltyMin,
      restitutionPenaltyMax: restitutionPenaltyNotFiltering
        ? 0
        : filters?.restitutionPenaltyMax,
    }
  }, [])

  const {
    items,
    isFetching,
    total,
    others,
    isFetchingOtherPages,
    page,
    rowsPerPage,
    refetch: fetchData,
    fetchAllData,
  } = useEntityData({
    newPage,
    limit: newRowsPerPage,
    filters: JSON.stringify(
      prepareFilters(filters, partnerInventoryFilters.filtersData)
    ),
    sort: filters.order?.column,
    direction: filters.order?.direction,
  })

  const { isUpdating: isUpdatingAction, updateInventory } = useInventory()

  const { selected, selectAllClick, selectClick, isSelected, resetSelected } =
    useCheckboxSelection(() => fetchAllData('inventory/'))

  const handleFilters = useDebouncedCallback(
    async () => fetchData('inventory/'),
    100
  )

  const numberFormatter = new Intl.NumberFormat('it-IT')

  useEffect(() => {
    if (isUpdatingAction) {
      return
    }
    handleFilters()
  }, [fetchData, newPage, newRowsPerPage, isUpdatingAction, handleFilters])

  useEffect(() => {
    if (!items.length || filters?.initialized) {
      return
    }
    initFilters(items)
  }, [items, initFilters, filters?.initialized])

  const arvalUser = useMemo(() => hasOneOfRoles(ROLES_ARVAL), [hasOneOfRoles])
  const arvalAdmin = useMemo(() => hasOneOfRoles(ROLE_ADMIN), [hasOneOfRoles])
  const isSuperAdmin = useMemo(
    () => hasOneOfRoles(ROLE_SUPER_ADMIN),
    [hasOneOfRoles]
  )
  const partnerAdmin = useMemo(
    () => hasOneOfRoles([ROLE_PARTNER_ADMIN]),
    [hasOneOfRoles]
  )
  const adminOrLogistic = useMemo(
    () => hasOneOfRoles([ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_LOGISTICS]),
    [hasOneOfRoles]
  )

  const b2c = useMemo(() => hasOneOfRoles([ROLE_B2C]), [hasOneOfRoles])
  const postSales = useMemo(
    () => hasOneOfRoles([ROLE_POST_SALES]),
    [hasOneOfRoles]
  )

  // const noActionStates = useMemo(() => {
  //   const result = [
  //     INVENTORY_STATE_SOLD,
  //     INVENTORY_STATE_PRESOLD,
  //     INVENTORY_STATE_ACQUIRED_B2B,
  //     INVENTORY_STATE_RESOLD_B2B,
  //   ]

  //   if (arvalAdmin || isSuperAdmin) while (result.length > 0) result.pop()
  //   if (!arvalUser) result.push(INVENTORY_STATE_RETURNED_BY_PARTNER)
  //   return result
  // }, [arvalAdmin, arvalUser, isSuperAdmin])

  const preparedItems = useMemo(
    () =>
      items.map((item) => {
        return {
          ...item,
          partner: <span id={`${item.partnerId}`}>{item.partnerName} </span>,
          daysToExpire: differenceInDays(new Date(item.expiresAt), new Date()),
          comments:
            others?.comments?.filter(
              (comment) => comment.inventoryId === item.inventoryId
            ) || [],
          // REL-F03: stream Release segregato, agganciato per riga come i NOTE.
          releaseComments:
            others?.releaseComments?.filter(
              (comment) => comment.inventoryId === item.inventoryId
            ) || [],
        }
      }),
    [items, others]
  )

  const readOnly = useMemo(
    () => isFetching || isFetchingOtherPages || isUpdatingAction,
    [isFetching, isFetchingOtherPages, isUpdatingAction]
  )

  const handleChangePage = (event, newPage) => {
    handleChangeTablePage(event, newPage)
    //resetSelected()
  }

  const handleChangeRowsPerPage = (event) => {
    handleChangeTableRowsPerPage(event)
    // resetSelected()
  }

  const handleSelectAllClick = useCallback(
    (event) => {
      selectAllClick(event)
    },
    [selectAllClick]
  )

  const handleRowClick = (row) => {
    selectClick(row.original, 'inventoryId')
  }

  const handleConfirmAction = async () => {
    if (
      action === ACTION_RETURN ||
      action === ACTION_EDIT_DATES ||
      action === ACTION_ADD_COMMENT ||
      action === ACTION_ADD_RELEASE_COMMENT ||
      action === ACTION_DOWNLOAD_REPORT ||
      action === ACTION_RESO_TECNICO ||
      // Forward Release (REL-F02): hanno un form (commento +/- outcome) → passano
      // dal submit di Formik. I reverse restano nel ramo else (nessun form).
      action === ACTION_IN_TRATTATIVA_RELEASE ||
      action === ACTION_VALUTAZIONE_RELEASE ||
      action === ACTION_VALIDATO_RISCHIO ||
      action === ACTION_RELEASE
    ) {
      formikRef.current.handleSubmit()
      return false
    }

    const expire =
      action === ACTION_UPDATE_EXPIRES && getYearMonthDayDate(dateValue)
    await updateInventory(
      action,
      selected.map((car) => ({
        inventoryId: car.inventoryId,
        expire: action === ACTION_UPDATE_EXPIRES ? expire : undefined,
      })),
      (action === ACTION_RETURN_FROM_ARVAL ||
        (action === ACTION_RETURN_FROM_PARTNER && partnerAdmin)) &&
        selected.every((car) => car.partnerRestoration)
        ? { restored }
        : undefined
    )
    await initFilters(items)
    resetSelected()
  }

  const handleReturnSubmit = async (values) => {
    const data = {
      date: values.returnedAt ? getYearMonthDayDate(values.returnedAt) : null,
      exitedAt: values.exitedAt ? getYearMonthDayDate(values.exitedAt) : null,
      comment: values.comment,
    }

    updateInventory(
      action,
      selected.map((car) => ({
        inventoryId: car.inventoryId,
      })),
      data
    )

    handleCloseConfirmationDialog()
    resetSelected()
  }

  const handleResoTecnicoSubmit = async (values) => {
    const data = {
      mode: values.mode,
      cluster: values.cluster,
      detail: values.cluster === 'meccanica' ? values.detail : undefined,
      comment: values.comment,
    }

    updateInventory(
      action,
      selected.map((car) => ({ inventoryId: car.inventoryId })),
      data
    )

    handleCloseConfirmationDialog()
    resetSelected()
  }

  // Submit unico per i 4 forward della lifecycle Release (REL-F02). `action` è già
  // il suffisso URL. Commento sempre; outcome solo per validato-rischio/release.
  const handleReleaseTransitionSubmit = async (values) => {
    const data = { comment: values.comment }
    if (values.outcome) {
      data.outcome = values.outcome
    }

    await updateInventory(
      action,
      selected.map((car) => ({ inventoryId: car.inventoryId })),
      data
    )

    handleCloseConfirmationDialog()
    await initFilters(items)
    resetSelected()
  }

  // REL-F03: aggiunta di un commento allo stream Release standalone. Invia solo
  // { comment } → BE PATCH /inventory/add-release-comment. initFilters rinfresca
  // così il nuovo commento appare subito nella timeline della colonna.
  const handleAddReleaseCommentSubmit = async (values) => {
    await updateInventory(
      ACTION_ADD_RELEASE_COMMENT,
      selected.map((car) => ({ inventoryId: car.inventoryId })),
      { comment: values.comment }
    )

    handleCloseConfirmationDialog()
    await initFilters(items)
    resetSelected()
  }

  const handleDownloadReportSubmit = async (values) => {
    setFormLoading(true)
    await saveReport(
      'report_inventory',
      getYearMonthDayDate(values.startDate),
      getYearMonthDayDate(values.endDate)
    )
    setFormLoading(false)
    handleCloseConfirmationDialog()
  }

  const {
    loading: confirmLoading,
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(handleConfirmAction)

  const handleOpenMenu = useCallback((event) => {
    setAnchorEl(event.currentTarget)
  }, [])

  // Abilitazioni condivise da Cristiano Valli
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const actionItemsEnabled = useMemo(() => {
    return {
      // Resa partner /return-from-partner
      // Ruoli: Partner admin, ~~**Partner read only**~~
      // Stati: Assegnata, Preassegnata, Prolungata, Scaduta
      ACTION_RETURN_FROM_PARTNER:
        partnerAdmin &&
        selected.every((car) =>
          [
            INVENTORY_STATE_ASSIGNED,
            INVENTORY_STATE_PREASSIGNED,
            INVENTORY_STATE_PROLONGED,
            INVENTORY_STATE_EXPIRED,
          ].includes(car.state)
        ),
      // Reverse Resa /return-to-partner
      // Ruoli: Admin, Super admin, ~~**Read only, Logistica**~~, B2C
      // Stati: Resa partner, Resa Arval
      ACTION_RETURN_TO_PARTNER:
        (arvalAdmin || isSuperAdmin || b2c) &&
        selected.every((car) =>
          [
            INVENTORY_STATE_RETURNED_BY_PARTNER,
            INVENTORY_STATE_RETURNED_BY_ARVAL,
          ].includes(car.state)
        ),
      // Prorogata /update-expires
      // Ruoli: Admin, Super admin, ~~**Read only, Logistica**~~, B2C
      // Stati: Assegnata, Preassegnata, ~~**Prolungata**~~ (questo no perchè si tratta dello stesso status), Scaduta
      ACTION_UPDATE_EXPIRES:
        (arvalAdmin || isSuperAdmin || b2c) &&
        selected.every((car) =>
          [
            INVENTORY_STATE_ASSIGNED,
            INVENTORY_STATE_PREASSIGNED,
            INVENTORY_STATE_EXPIRED,
          ].includes(car.state)
        ),
      // Resa Arval /return-from-arval
      // Ruoli: Admin, Super admin, ~~**Read only, Logistica**~~, B2C
      // Stati: Assegnata, Preassegnata, Prolungata, Scaduta
      ACTION_RETURN_FROM_ARVAL:
        (arvalAdmin || isSuperAdmin || b2c) &&
        // eslint-disable-next-line sonarjs/no-identical-functions
        selected.every((car) =>
          [
            INVENTORY_STATE_ASSIGNED,
            INVENTORY_STATE_PREASSIGNED,
            INVENTORY_STATE_EXPIRED,
            INVENTORY_STATE_PROLONGED,
          ].includes(car.state)
        ),
      // Restituita /return
      // Ruoli: Admin, Super admin, ~~**Read only, Logistica**~~, B2C
      // Stati: Assegnata, Preassegnata, Prolungata, Scaduta, Resa partner, Resa Arval
      ACTION_RETURN:
        (arvalAdmin || isSuperAdmin || b2c) &&
        selected.every((car) =>
          [
            INVENTORY_STATE_ASSIGNED,
            INVENTORY_STATE_PREASSIGNED,
            INVENTORY_STATE_EXPIRED,
            INVENTORY_STATE_PROLONGED,
            INVENTORY_STATE_RETURNED_BY_PARTNER,
            INVENTORY_STATE_RETURNED_BY_ARVAL,
          ].includes(car.state)
        ),
      // Modifica date /edit-dates
      // Ruoli: Admin, Super admin, ~~**Logistica**~~
      // Condizioni: Singola auto selezionata e Data ingresso o uscita non vuote
      ACTION_EDIT_DATES:
        (arvalAdmin || isSuperAdmin || b2c) &&
        selected.length === 1 &&
        (selected[0].enteredAt || selected[0].exitedAt),
      // Reverse Ripristino /restoration-reverse
      // Ruoli: Admin, Super admin, ~~**Read only, Logistica**~~, B2C
      // Stati: Resa partner, Resa Arval
      ACTION_RESTORATION_REVERSE:
        (arvalAdmin || isSuperAdmin || b2c) &&
        selected.every(
          (car) =>
            [
              INVENTORY_STATE_RETURNED_BY_PARTNER,
              INVENTORY_STATE_RETURNED,
            ].includes(car.state) && car.restored !== null
        ),
      // Parcheggiata /parked
      // Ruoli: ~~**Tutti**~~ solo i Partner admin e gli Admin, Super admin, B2C di Arval (solo come eventuale backup)
      // Stati: Assegnata, Prolungata, Scaduta, Resa partner, Resa Arval
      // Vincolo giorni: Partner nell'intera finestra 41..90; Arval nessun vincolo temporale (sempre)
      ACTION_PARKED:
        ((arvalAdmin || isSuperAdmin || b2c) &&
          selected.every(
            (car) =>
              [
                INVENTORY_STATE_ASSIGNED,
                INVENTORY_STATE_EXPIRED,
                INVENTORY_STATE_RETURNED_BY_PARTNER,
                INVENTORY_STATE_PROLONGED,
                INVENTORY_STATE_RETURNED_BY_ARVAL,
              ].includes(car.state) &&
              car.parkedStatusAllowed &&
              !car.mandatoryToBuy &&
              car.blindOrigin === BLIND_ORIGIN_NO_BLIND
          )) ||
        (partnerAdmin &&
          selected.every((car) => {
            const days = differenceInDays(
              new Date(),
              new Date(car.availabilityAt)
            )
            return (
              [
                INVENTORY_STATE_ASSIGNED,
                INVENTORY_STATE_EXPIRED,
                INVENTORY_STATE_RETURNED_BY_PARTNER,
                INVENTORY_STATE_PROLONGED,
                INVENTORY_STATE_RETURNED_BY_ARVAL,
              ].includes(car.state) &&
              car.parkedStatusAllowed &&
              !car.mandatoryToBuy &&
              car.blindOrigin === BLIND_ORIGIN_NO_BLIND &&
              car.canMoveToParked &&
              days >= PARKED_DAY_MIN &&
              days <= PARKED_DAY_MAX
            )
          })),
      // Reverse Parcheggiata /parked-reverse
      // Ruoli: Admin, Super admin, ~~**Read only, Logistica,**~~ B2C
      // Stati: Parcheggiata
      ACTION_PARKED_REVERSE:
        (arvalAdmin || isSuperAdmin || b2c) &&
        selected.every(
          (car) =>
            [INVENTORY_STATE_PARKED].includes(car.state) &&
            car.parkedStatusAllowed
        ),
      // Reso Tecnico /reso-tecnico
      // Ruoli: Admin, Super admin, B2C (Arval) senza vincolo temporale; Partner Admin nella finestra giorni
      // Stati: solo Assegnata
      ACTION_RESO_TECNICO:
        ((arvalAdmin || isSuperAdmin || b2c) &&
          selected.every((car) =>
            [INVENTORY_STATE_ASSIGNED].includes(car.state)
          )) ||
        (partnerAdmin &&
          selected.every((car) => {
            const days = differenceInDays(
              new Date(),
              new Date(car.availabilityAt)
            )
            return (
              [INVENTORY_STATE_ASSIGNED].includes(car.state) &&
              // Regola business (2026-07-23): sui Blind (obbligo d'acquisto) il
              // reso tecnico è riservato ad Arval → il partner non lo vede.
              // L'autorità resta il BE; qui è solo UX (come il gate parcheggiata).
              car.blindOrigin === BLIND_ORIGIN_NO_BLIND &&
              car.resoTecnicoAllowed &&
              days >= RESO_TECNICO_DAY_MIN &&
              days <= car.resoTecnicoDayLimit
            )
          })),
      // Aggiungi Nota /parked-reverse
      // Ruoli: Admin, Super admin, Logistica, B2C
      // Stati: possibile da tutti gli stati

      ACTION_ADD_COMMENT: adminOrLogistic || b2c || postSales,

      // REL-F03: stesso set di NOTE (BE add-release-comment ha lo stesso guard
      // requiresArvalAdminOrB2CUserOrPostSalesUser).
      ACTION_ADD_RELEASE_COMMENT: adminOrLogistic || b2c || postSales,

      // ─── Lifecycle Release (REL-F02) ──────────────────────────────────────
      // Forward (#1/#3/#5/#7): la lifecycle è guidata dal partner, con Arval
      // admin/superAdmin/b2c come backup (OPEN-A → partnerAdmin + Arval backup).
      // Il BE è `userAuthorize` (qualsiasi write); qui è solo UX.
      // REL-F07 (teamRelease read-only): nessuna esclusione esplicita necessaria.
      // `accountType` è un singolo ruolo, quindi un utente teamRelease valuta
      // false su tutti i booleani usati qui (partnerAdmin/arvalAdmin/isSuperAdmin/
      // b2c) → le azioni Release spariscono per costruzione (allow-list). Il
      // render read-only è gestito a monte via `contentIsReadOnly`
      // (PartnerInventory.js), che rimuove selezione e menu azioni.
      //
      // #1 Entra In trattativa: Assegnata/Preassegnata/Prolungata + Flag Release
      ACTION_IN_TRATTATIVA_RELEASE:
        (partnerAdmin || arvalAdmin || isSuperAdmin || b2c) &&
        selected.every(
          (car) =>
            [
              INVENTORY_STATE_ASSIGNED,
              INVENTORY_STATE_PREASSIGNED,
              INVENTORY_STATE_PROLONGED,
            ].includes(car.state) && car.releaseFlag === true
        ),
      // #2 Reverse In trattativa (partner-write, come i forward)
      ACTION_IN_TRATTATIVA_RELEASE_REVERSE:
        (partnerAdmin || arvalAdmin || isSuperAdmin || b2c) &&
        selected.every(
          (car) => car.state === INVENTORY_STATE_IN_TRATTATIVA_RELEASE
        ),
      // #3 Entra Valutazione
      ACTION_VALUTAZIONE_RELEASE:
        (partnerAdmin || arvalAdmin || isSuperAdmin || b2c) &&
        selected.every(
          (car) => car.state === INVENTORY_STATE_IN_TRATTATIVA_RELEASE
        ),
      // #4 Reverse Valutazione (Arval Admin/B2C only)
      ACTION_VALUTAZIONE_RELEASE_REVERSE:
        (arvalAdmin || isSuperAdmin || b2c) &&
        selected.every(
          (car) => car.state === INVENTORY_STATE_VALUTAZIONE_RELEASE
        ),
      // #5 Validato Rischio (dialog: outcome ok/ko + commento)
      ACTION_VALIDATO_RISCHIO:
        (partnerAdmin || arvalAdmin || isSuperAdmin || b2c) &&
        selected.every(
          (car) => car.state === INVENTORY_STATE_VALUTAZIONE_RELEASE
        ),
      // #6 Reverse Validato (Arval Admin/B2C only)
      ACTION_VALIDATO_RISCHIO_REVERSE:
        (arvalAdmin || isSuperAdmin || b2c) &&
        selected.every((car) => car.state === INVENTORY_STATE_VALIDATO_RISCHIO),
      // #7 Release (dialog: outcome confirm/rinuncia + commento)
      ACTION_RELEASE:
        (partnerAdmin || arvalAdmin || isSuperAdmin || b2c) &&
        selected.every((car) => car.state === INVENTORY_STATE_VALIDATO_RISCHIO),
      // #8 Reverse Release (Arval Admin/B2C only)
      ACTION_RELEASE_REVERSE:
        (arvalAdmin || isSuperAdmin || b2c) &&
        selected.every((car) => car.state === INVENTORY_STATE_RELEASE),
    }
  }, [
    selected,
    adminOrLogistic,
    b2c,
    arvalAdmin,
    isSuperAdmin,
    partnerAdmin,
    postSales,
  ])

  // Il dealer/partner ha la facoltà di mettere in Parcheggiata i veicoli?
  // Se NON ce l'ha, l'unico reso possibile è quello fisico: in quel caso il
  // pop-up non mostra la scelta della "Modalità di reso" (unica opzione
  // sottoscrivibile) e va direttamente al Cluster danno (requisito business).
  const partnerCanPark = useMemo(
    () => selected.every((car) => car.parkedStatusAllowed),
    [selected]
  )

  // Reso tecnico: il reso "virtuale" riusa il regime parcheggiata, quindi è
  // disponibile solo se il partner ha la parcheggiata abilitata. Se non ce l'ha,
  // si può fare solo il reso "fisico" (spec: foglio "schema" del requisito).
  // Inoltre un'auto Blind (obbligo d'acquisto) ammette solo il reso fisico:
  // l'autorità resta il BE (HANDOFF FIX 1 §3.3), qui è solo UX.
  const canResoTecnicoVirtual = useMemo(
    () =>
      selected.every(
        (car) =>
          car.parkedStatusAllowed && car.blindOrigin === BLIND_ORIGIN_NO_BLIND
      ),
    [selected]
  )

  // Motivazione mostrata quando il reso virtuale non è disponibile: se è per via
  // di un'auto Blind il messaggio è specifico, altrimenti è quello parcheggiata.
  const hasBlindSelected = useMemo(
    () => selected.some((car) => car.blindOrigin !== BLIND_ORIGIN_NO_BLIND),
    [selected]
  )

  const columns = useMemo(
    () =>
      createColumns(
        items,
        arvalUser,
        others?.countExpired,
        // noActionStates,
        isSelected,
        handleSelectAllClick,
        selected,
        readOnly,
        contentIsReadOnly,
        partnerInventoryFilters,
        t
      ),
    [
      items,
      arvalUser,
      others?.countExpired,
      // noActionStates,
      isSelected,
      handleSelectAllClick,
      selected,
      readOnly,
      contentIsReadOnly,
      partnerInventoryFilters,
      t,
    ]
  )

  const handleDownload = async () => {
    const params = {
      page: 0,
      limit: total,
      filters: JSON.stringify(
        prepareFilters(filters, partnerInventoryFilters.filtersData)
      ),
      sort: filters.order?.column,
      direction: filters.order?.direction,
    }
    await saveExport({ name: 'inventory', params, isInventory: true })
  }

  /**
   * @type {React.RefObject<import('formik').FormikProps<{}>>}
   */
  const formikRef = React.useRef(null)
  const [formLoading, setFormLoading] = React.useState(false)

  return (
    <>
      <LoadingBackdrop open={isFetching || isFetchingOtherPages} />
      <Table
        //isFetching={true}
        //isFetchingOtherPages={isFetchingOtherPages}
        items={preparedItems}
        columns={columns}
        total={total}
        hasPagination={true}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        getRowProps={(row) => {
          const noAction = contentIsReadOnly
          return {
            onClick: (e) => {
              !noAction && handleRowClick(row)
            },
            sx: { cursor: !noAction ? 'pointer' : undefined },
          }
        }}
        sortable
        excludedSortableIds={[
          'checkbox',
          'options',
          'mandatoryToBuy',
          'comments',
          'releaseComments',
          'resoTecnico',
          'releaseFlag',
          'opzioneReleaseAt',
          'releaseAt',
        ]}
        orderColumn={filters?.order?.column}
        orderDirection={filters?.order?.direction}
        handleSort={setSort}
        stickyHeader
        bodyMaxHeight={`calc(100vh - ${80 + 20 + 32 + 73 + 20 + 60 + 20}px)`} //mainPaddingTop + pagePaddingTop + title + titleMarginBottom + tableHeader + tableMarginBottom + pagePaddingBottom
        top={
          <>
            <ConfirmBar
              component="div"
              hideConfirm
              left={
                <>
                  <SearchField
                    data-testid="text-search"
                    placeholder={t('common.form.searchBy')}
                    value={filters.query}
                    onChange={(event) => setSearchQuery(event.target.value)}
                  />
                </>
              }
              leftProps={{ sx: { width: 0.5 } }}
              right={
                <>
                  <Box pr={2} />
                  <Typography
                    variant="body2"
                    color="textPrimary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {t('partnerInventory.stock')}
                  </Typography>
                  <Box pr={1} />
                  <Typography
                    variant="body2"
                    color="textPrimary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <strong>
                      {numberFormatter.format(others?.stock || 0)}
                    </strong>
                  </Typography>
                  <Box pr={2} />
                  <Typography
                    variant="body2"
                    color="textPrimary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {t('partnerInventory.value')}{' '}
                  </Typography>
                  <Box pr={1} />
                  <Typography
                    variant="body2"
                    color="textPrimary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <strong>
                      {`€ ${numberFormatter.format(others?.value || 0)}`}
                    </strong>
                  </Typography>
                  <Box pr={2} />
                  {arvalUser && (
                    <>
                      <Collapse orientation="horizontal" in={isFetchingReport}>
                        <div style={{ marginLeft: 4, marginRight: 4 }}>
                          <CircularProgress size={20} />
                        </div>
                      </Collapse>
                      <ClipboardWithTrendUp
                        disabled={isFetchingReport}
                        handleClick={() => {
                          setAction(ACTION_DOWNLOAD_REPORT)

                          // Set the start date from a year ago
                          const now = new Date()
                          setInitialDownloadReportValues({
                            startDate: addYears(now, -1),
                            endDate: now,
                          })
                          handleOpenConfirmationDialog()
                        }}
                      />
                      <Collapse orientation="horizontal" in={isFetchingLogs}>
                        <div style={{ marginLeft: 4, marginRight: 4 }}>
                          <CircularProgress size={20} />
                        </div>
                      </Collapse>
                      <LogIcon
                        disabled={isFetchingLogs}
                        handleClick={() =>
                          saveLogs(`logs_partner_inventory`, {
                            area: t('common.logs.partner-inventory'),
                          })
                        }
                      />
                    </>
                  )}
                  <Collapse orientation="horizontal" in={isFetchingExport}>
                    <div style={{ marginLeft: 4, marginRight: 4 }}>
                      <CircularProgress size={20} />
                    </div>
                  </Collapse>
                  <DownloadBar
                    readOnly={readOnly}
                    handleDownload={handleDownload}
                    disabled={isFetchingExport}
                  />
                  <Collapse
                    orientation="horizontal"
                    in={isFetchingReleaseReport}
                  >
                    <div style={{ marginLeft: 4, marginRight: 4 }}>
                      <CircularProgress size={20} />
                    </div>
                  </Collapse>
                  <ReleaseReportBar
                    disabled={isFetchingReleaseReport}
                    handleDownload={() => saveReleaseReport('release_report')}
                  />
                  <IconButton
                    data-testid="options-action"
                    aria-label="More"
                    onClick={handleOpenMenu}
                    disabled={
                      !selected.length ||
                      readOnly ||
                      !Object.values(actionItemsEnabled).filter((i) => i).length
                    }
                  >
                    <MoreIcon />
                  </IconButton>
                </>
              }
            />
            <Divider />
          </>
        }
      />
      <Menu
        data-testid={`action-menu`}
        id="options-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        {actionItemsEnabled.ACTION_RETURN_FROM_PARTNER && (
          <MenuItem
            data-testid={`action-menu-return-from-partner`}
            onClick={() => {
              setAnchorEl(null)
              setRestored(false)
              setAction(ACTION_RETURN_FROM_PARTNER)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.returnFromPartner')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_RETURN_TO_PARTNER && (
          <MenuItem
            data-testid={`action-menu-return-to-partner`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_RETURN_TO_PARTNER)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.returnToPartner')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_UPDATE_EXPIRES && (
          <MenuItem
            data-testid={`action-menu-update-expires`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_UPDATE_EXPIRES)
              const startDate = new Date(
                Math.max(...selected.map((car) => new Date(car.expiresAt)))
              )
              startDate.setDate(startDate.getDate() + 1)

              setDateValue(startDate)
              setStartDateValue(startDate)

              handleOpenConfirmationDialog()
            }}
          >
            {t(
              `partnerInventory.menu.updateExpires${
                selected.every((car) =>
                  [INVENTORY_STATE_PROLONGED].includes(car.state)
                )
                  ? 'Alternative'
                  : ''
              }`
            )}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_RETURN_FROM_ARVAL && (
          <MenuItem
            data-testid={`action-menu-return-from-arval`}
            onClick={() => {
              setAnchorEl(null)
              setRestored(false)
              setAction(ACTION_RETURN_FROM_ARVAL)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.returnFromArval')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_RETURN && (
          <MenuItem
            data-testid={`action-menu-return`}
            onClick={() => {
              setAnchorEl(null)
              setInitialDateValues({
                returnedAt: null,
                exitedAt: null,
                comment: '',
              })
              setAction(ACTION_RETURN)

              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.return')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_EDIT_DATES && (
          <MenuItem
            data-testid={`action-menu-edit-dates`}
            onClick={() => {
              setAnchorEl(null)
              setInitialDateValues({
                returnedAt: selected[0].enteredAt
                  ? new Date(selected[0].enteredAt)
                  : null,
                exitedAt: selected[0].exitedAt
                  ? new Date(selected[0].exitedAt)
                  : null,
                comment: '',
              })
              setAction(ACTION_EDIT_DATES)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.editDates')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_RESTORATION_REVERSE && (
          <MenuItem
            data-testid={`action-menu-restoration-reverse`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_RESTORATION_REVERSE)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.restorationReverse')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_PARKED && (
          <MenuItem
            data-testid={`action-menu-parked`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_PARKED)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.parked')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_PARKED_REVERSE && (
          <MenuItem
            data-testid={`action-menu-reverse-parked`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_PARKED_REVERSE)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.reverse-parked')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_RESO_TECNICO && (
          <MenuItem
            data-testid={`action-menu-reso-tecnico`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_RESO_TECNICO)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.reso-tecnico')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_ADD_COMMENT && (
          <MenuItem
            data-testid={`action-menu-add-comment`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_ADD_COMMENT)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.add-comment')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_ADD_RELEASE_COMMENT && (
          <MenuItem
            data-testid={`action-menu-add-release-comment`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_ADD_RELEASE_COMMENT)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.add-release-comment')}
          </MenuItem>
        )}
        {/* ─── Lifecycle Release (REL-F02): reverse tenuti vicino al forward ─── */}
        {actionItemsEnabled.ACTION_IN_TRATTATIVA_RELEASE && (
          <MenuItem
            data-testid={`action-menu-in-trattativa-release`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_IN_TRATTATIVA_RELEASE)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.in-trattativa-release')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_IN_TRATTATIVA_RELEASE_REVERSE && (
          <MenuItem
            data-testid={`action-menu-in-trattativa-release-reverse`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_IN_TRATTATIVA_RELEASE_REVERSE)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.in-trattativa-release-reverse')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_VALUTAZIONE_RELEASE && (
          <MenuItem
            data-testid={`action-menu-valutazione-release`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_VALUTAZIONE_RELEASE)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.valutazione-release')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_VALUTAZIONE_RELEASE_REVERSE && (
          <MenuItem
            data-testid={`action-menu-valutazione-release-reverse`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_VALUTAZIONE_RELEASE_REVERSE)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.valutazione-release-reverse')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_VALIDATO_RISCHIO && (
          <MenuItem
            data-testid={`action-menu-validato-rischio`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_VALIDATO_RISCHIO)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.validato-rischio')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_VALIDATO_RISCHIO_REVERSE && (
          <MenuItem
            data-testid={`action-menu-validato-rischio-reverse`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_VALIDATO_RISCHIO_REVERSE)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.validato-rischio-reverse')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_RELEASE && (
          <MenuItem
            data-testid={`action-menu-release`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_RELEASE)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.release')}
          </MenuItem>
        )}
        {actionItemsEnabled.ACTION_RELEASE_REVERSE && (
          <MenuItem
            data-testid={`action-menu-release-reverse`}
            onClick={() => {
              setAnchorEl(null)
              setAction(ACTION_RELEASE_REVERSE)
              handleOpenConfirmationDialog()
            }}
          >
            {t('partnerInventory.menu.release-reverse')}
          </MenuItem>
        )}
      </Menu>
      {/**  Edit here */}
      <ConfirmationDialog
        title={t(
          `partnerInventory.action.${action}${
            action === ACTION_UPDATE_EXPIRES &&
            selected.every((car) =>
              [INVENTORY_STATE_PROLONGED].includes(car.state)
            )
              ? '.alternative'
              : ''
          }.title`
        )}
        contentComponent="div"
        content={
          <>
            <Typography variant="body2">
              {action !== ACTION_ADD_COMMENT &&
                action !== ACTION_ADD_RELEASE_COMMENT && (
                  <Trans
                    i18nKey={`partnerInventory.action.${action}.content`}
                    values={{
                      attributeByPartner: t(
                        'partnerInventory.status.returned_by_partner'
                      ),
                      attributeByArval: t(
                        'partnerInventory.status.returned_by_arval'
                      ),
                      attributeReturn: t('partnerInventory.status.returned'),
                    }}
                    components={{ bold: <strong /> }}
                  />
                )}
            </Typography>
            {((action === ACTION_RETURN_FROM_PARTNER && partnerAdmin) ||
              action === ACTION_RETURN_FROM_ARVAL) &&
              selected.every((item) => item?.partnerRestoration) && (
                <FormControlLabel
                  control={
                    <LabelledSwitch
                      data-testid="restored"
                      disabled={false}
                      checked={restored}
                      onChange={(e) => setRestored((s) => !s)}
                    />
                  }
                  label={
                    <Typography color="textPrimary" variant="body2">
                      {t(`partnerInventory.action.${action}.restored`)}
                    </Typography>
                  }
                  labelPlacement="start"
                  sx={{ ml: 0 }}
                />
              )}
            {action === ACTION_RETURN_FROM_ARVAL &&
              !selected.every((item) => item?.partnerRestoration) && (
                <Typography color="textPrimary" variant="body2" pt={2}>
                  {t(`partnerInventory.action.${action}.restoredDisabled`)}
                </Typography>
              )}
            {/* {action === ACTION_RESTORATION_REVERSE && (
              <FormControlLabel
                control={
                  <LabelledSwitch
                    data-testid="restored"
                    disabled={false}
                    checked={restored}
                    onChange={(e) => setRestored((s) => !s)}
                  />
                }
                label={
                  <Typography color="textPrimary" variant="body2">
                    {t(`partnerInventory.action.${action}.restored`)}
                  </Typography>
                }
                labelPlacement="start"
                sx={{ ml: 0 }}
              />
            )} */}
            {action === ACTION_UPDATE_EXPIRES && (
              <StaticDatePicker
                value={dateValue}
                onChange={setDateValue}
                minDate={startDateValue}
              />
            )}
            {(action === ACTION_RETURN || action === ACTION_EDIT_DATES) && (
              <Formik
                innerRef={formikRef}
                validationSchema={DatesSchema}
                enableReinitialize={true}
                onSubmit={handleReturnSubmit}
                initialValues={initialDateValues}
              >
                {({ values }) => (
                  <Form>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      {action === ACTION_RETURN ? (
                        <>
                          <FormDatePicker
                            name="returnedAt"
                            label={t(
                              `partnerInventory.action.${action}.returnedDate`
                            )}
                            minDate={values.exitedAt}
                            data-testid="returned-at"
                          />

                          <FormDatePicker
                            name="exitedAt"
                            label={t(
                              `partnerInventory.action.${action}.exitDate`
                            )}
                            maxDate={values.returnedAt}
                            disabled={!values.returnedAt}
                            data-testid="exited-at"
                          />
                        </>
                      ) : (
                        <>
                          <FormDatePicker
                            name="exitedAt"
                            label={t(
                              `partnerInventory.action.${action}.exitDate`
                            )}
                            maxDate={values.returnedAt}
                            disabled={!initialDateValues.exitedAt}
                            data-testid="exited-at"
                          />
                          <FormDatePicker
                            name="returnedAt"
                            label={t(
                              `partnerInventory.action.${action}.returnedDate`
                            )}
                            minDate={values.exitedAt}
                            disabled={!initialDateValues.returnedAt}
                            data-testid="returned-at"
                          />
                        </>
                      )}

                      <FormTextField
                        name="comment"
                        placeholder={t(
                          'partnerInventory.action.return.comment'
                        )}
                        multiline
                        fullWidth
                        rows={2}
                        rowsmax={4}
                        data-testid="comment"
                      />
                    </Box>
                  </Form>
                )}
              </Formik>
            )}
            {action === ACTION_ADD_COMMENT && (
              <Formik
                innerRef={formikRef}
                validationSchema={DatesSchema}
                enableReinitialize={true}
                onSubmit={handleReturnSubmit}
                initialValues={initialDateValues}
              >
                {({ values }) => (
                  <Form>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 0,
                      }}
                    >
                      <FormTextField
                        name="comment"
                        placeholder={t(
                          'partnerInventory.action.add-comment.comment'
                        )}
                        multiline
                        fullWidth
                        rows={3}
                        rowsmax={4}
                        data-testid="note"
                      />
                    </Box>
                  </Form>
                )}
              </Formik>
            )}
            {action === ACTION_ADD_RELEASE_COMMENT && (
              <Formik
                innerRef={formikRef}
                validationSchema={AddReleaseCommentSchema}
                enableReinitialize={true}
                onSubmit={handleAddReleaseCommentSubmit}
                initialValues={{ comment: '' }}
              >
                {() => (
                  <Form>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 0,
                      }}
                    >
                      <FormTextField
                        name="comment"
                        placeholder={t(
                          'partnerInventory.action.add-release-comment.comment'
                        )}
                        multiline
                        fullWidth
                        rows={3}
                        rowsmax={4}
                        data-testid="release-note"
                      />
                    </Box>
                  </Form>
                )}
              </Formik>
            )}
            {action === ACTION_DOWNLOAD_REPORT && (
              <Formik
                innerRef={formikRef}
                validationSchema={DownloadReportFormSchema}
                enableReinitialize={true}
                onSubmit={handleDownloadReportSubmit}
                initialValues={initialDownloadReportValues}
              >
                {({ values }) => (
                  <Form>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      <FormDatePicker
                        name="startDate"
                        label={t(`partnerInventory.action.${action}.startDate`)}
                        minDate={addYears(values.endDate, -1)}
                        data-testid="start-date"
                      />
                      <FormDatePicker
                        name="endDate"
                        label={t(`partnerInventory.action.${action}.endDate`)}
                        maxDate={addYears(values.startDate, 1)}
                        data-testid="end-date"
                      />
                    </Box>
                  </Form>
                )}
              </Formik>
            )}
            {action === ACTION_RESO_TECNICO && (
              <Formik
                innerRef={formikRef}
                validationSchema={ResoTecnicoSchema}
                enableReinitialize={true}
                onSubmit={handleResoTecnicoSubmit}
                initialValues={{
                  // Se il partner non può parcheggiare, l'unico reso è quello
                  // fisico: preselezioniamo "physical" e nascondiamo la scelta.
                  mode: partnerCanPark ? '' : 'physical',
                  cluster: '',
                  detail: '',
                  comment: '',
                }}
              >
                {({ values, setFieldValue, touched, errors }) => (
                  <Form>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      {/*
                        Modalità di reso: mostrata solo se il partner può
                        parcheggiare (quindi esiste davvero una scelta tra reso
                        fisico e virtuale). Se non può parcheggiare, l'unico reso
                        possibile è quello fisico (già preselezionato): non ha
                        senso mostrare la selezione con un'unica opzione, quindi
                        si va direttamente al Cluster danno.
                      */}
                      {partnerCanPark && (
                        <FormControl
                          component="fieldset"
                          error={touched.mode && !!errors.mode}
                        >
                          <FormLabel component="legend">
                            {t(
                              'partnerInventory.action.reso-tecnico.modeLabel'
                            )}
                          </FormLabel>
                          <RadioGroup
                            name="mode"
                            value={values.mode}
                            onChange={(e) =>
                              setFieldValue('mode', e.target.value)
                            }
                          >
                            <FormControlLabel
                              value="physical"
                              control={<Radio />}
                              label={t(
                                'partnerInventory.action.reso-tecnico.modePhysical'
                              )}
                            />
                            {canResoTecnicoVirtual && (
                              <FormControlLabel
                                value="virtual"
                                control={<Radio />}
                                label={t(
                                  'partnerInventory.action.reso-tecnico.modeVirtual'
                                )}
                              />
                            )}
                          </RadioGroup>
                          {!canResoTecnicoVirtual && (
                            <Typography variant="caption" color="textSecondary">
                              {t(
                                hasBlindSelected
                                  ? 'partnerInventory.action.reso-tecnico.virtualNotAllowedBlind'
                                  : 'partnerInventory.action.reso-tecnico.virtualNotAllowed'
                              )}
                            </Typography>
                          )}
                          {touched.mode && errors.mode && (
                            <Typography variant="caption" color="error">
                              {errors.mode}
                            </Typography>
                          )}
                        </FormControl>
                      )}

                      <FormControl
                        component="fieldset"
                        error={touched.cluster && !!errors.cluster}
                      >
                        <FormLabel component="legend">
                          {t(
                            'partnerInventory.action.reso-tecnico.clusterLabel'
                          )}
                        </FormLabel>
                        <RadioGroup
                          name="cluster"
                          value={values.cluster}
                          onChange={(e) => {
                            setFieldValue('cluster', e.target.value)
                            setFieldValue('detail', '')
                          }}
                        >
                          <FormControlLabel
                            value="carrozzeria"
                            control={<Radio />}
                            label={t(
                              'partnerInventory.action.reso-tecnico.clusterCarrozzeria'
                            )}
                          />
                          <FormControlLabel
                            value="meccanica"
                            control={<Radio />}
                            label={t(
                              'partnerInventory.action.reso-tecnico.clusterMeccanica'
                            )}
                          />
                        </RadioGroup>
                        {touched.cluster && errors.cluster && (
                          <Typography variant="caption" color="error">
                            {errors.cluster}
                          </Typography>
                        )}
                      </FormControl>

                      {values.cluster === 'meccanica' && (
                        <FormControl
                          fullWidth
                          error={touched.detail && !!errors.detail}
                        >
                          <InputLabel>
                            {t(
                              'partnerInventory.action.reso-tecnico.detailLabel'
                            )}
                          </InputLabel>
                          <Select
                            name="detail"
                            value={values.detail}
                            label={t(
                              'partnerInventory.action.reso-tecnico.detailLabel'
                            )}
                            onChange={(e) =>
                              setFieldValue('detail', e.target.value)
                            }
                            data-testid="reso-tecnico-detail"
                          >
                            <MenuItem value="motore">
                              {t(
                                'partnerInventory.action.reso-tecnico.detailMotore'
                              )}
                            </MenuItem>
                            <MenuItem value="cambio">
                              {t(
                                'partnerInventory.action.reso-tecnico.detailCambio'
                              )}
                            </MenuItem>
                            <MenuItem value="sistemi_sicurezza">
                              {t(
                                'partnerInventory.action.reso-tecnico.detailSistemiSicurezza'
                              )}
                            </MenuItem>
                            <MenuItem value="altri_danni">
                              {t(
                                'partnerInventory.action.reso-tecnico.detailAltriDanni'
                              )}
                            </MenuItem>
                          </Select>
                          {touched.detail && errors.detail && (
                            <Typography variant="caption" color="error">
                              {errors.detail}
                            </Typography>
                          )}
                        </FormControl>
                      )}

                      <FormTextField
                        name="comment"
                        placeholder={t(
                          'partnerInventory.action.reso-tecnico.comment'
                        )}
                        multiline
                        fullWidth
                        rows={2}
                        rowsmax={4}
                        data-testid="reso-tecnico-comment"
                      />
                    </Box>
                  </Form>
                )}
              </Formik>
            )}
            {/*
              Forward lifecycle Release (REL-F02): un unico Formik per i 4
              forward. Commento sempre obbligatorio; l'outcome (RadioGroup) è
              mostrato solo per Validato Rischio e Release. I reverse non hanno
              form: usano il solo <Trans> del content qui sopra.
            */}
            {[
              ACTION_IN_TRATTATIVA_RELEASE,
              ACTION_VALUTAZIONE_RELEASE,
              ACTION_VALIDATO_RISCHIO,
              ACTION_RELEASE,
            ].includes(action) && (
              <Formik
                innerRef={formikRef}
                validationSchema={ReleaseTransitionSchema}
                enableReinitialize={true}
                onSubmit={handleReleaseTransitionSubmit}
                initialValues={{ comment: '', outcome: '' }}
              >
                {({ values, setFieldValue, touched, errors }) => (
                  <Form>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      {/*
                        Outcome: solo Validato Rischio (ok/ko) e Release
                        (confirm/rinuncia). I valori restano invariati (enum BE);
                        si traducono solo le label. Template = reso-tecnico.
                      */}
                      {(action === ACTION_VALIDATO_RISCHIO ||
                        action === ACTION_RELEASE) && (
                        <FormControl
                          component="fieldset"
                          error={touched.outcome && !!errors.outcome}
                        >
                          <FormLabel component="legend">
                            {t(
                              `partnerInventory.action.${action}.outcomeLabel`
                            )}
                          </FormLabel>
                          <RadioGroup
                            name="outcome"
                            value={values.outcome}
                            onChange={(e) =>
                              setFieldValue('outcome', e.target.value)
                            }
                          >
                            {action === ACTION_VALIDATO_RISCHIO ? (
                              <>
                                <FormControlLabel
                                  value="ok"
                                  control={
                                    <Radio
                                      inputProps={{
                                        'data-testid': 'outcome-ok',
                                      }}
                                    />
                                  }
                                  label={t(
                                    'partnerInventory.action.validato-rischio.outcomeOk'
                                  )}
                                />
                                <FormControlLabel
                                  value="ko"
                                  control={
                                    <Radio
                                      inputProps={{
                                        'data-testid': 'outcome-ko',
                                      }}
                                    />
                                  }
                                  label={t(
                                    'partnerInventory.action.validato-rischio.outcomeKo'
                                  )}
                                />
                              </>
                            ) : (
                              <>
                                <FormControlLabel
                                  value="confirm"
                                  control={
                                    <Radio
                                      inputProps={{
                                        'data-testid': 'outcome-confirm',
                                      }}
                                    />
                                  }
                                  label={t(
                                    'partnerInventory.action.release.outcomeConfirm'
                                  )}
                                />
                                <FormControlLabel
                                  value="rinuncia"
                                  control={
                                    <Radio
                                      inputProps={{
                                        'data-testid': 'outcome-rinuncia',
                                      }}
                                    />
                                  }
                                  label={t(
                                    'partnerInventory.action.release.outcomeRinuncia'
                                  )}
                                />
                              </>
                            )}
                          </RadioGroup>
                          {touched.outcome && errors.outcome && (
                            <Typography variant="caption" color="error">
                              {errors.outcome}
                            </Typography>
                          )}
                        </FormControl>
                      )}

                      <FormTextField
                        name="comment"
                        placeholder={t(
                          `partnerInventory.action.${action}.comment`
                        )}
                        multiline
                        fullWidth
                        rows={2}
                        rowsmax={4}
                        data-testid="release-comment"
                      />
                    </Box>
                  </Form>
                )}
              </Formik>
            )}
          </>
        }
        readOnly={confirmLoading || formLoading}
        open={openConfirmationDialog}
        handleClose={handleCloseConfirmationDialog}
        handleSubmit={handleConfirmConfirmationDialog}
      />
    </>
  )
}

PartnerInventoryTable.propTypes = {
  contentIsReadOnly: T.bool,
}

export default PartnerInventoryTable
